<template lang="pug">
    .timeline-view
        //- Legend
        .timeline-view__legend
            .timeline-view__legend-item
                .timeline-view__legend-dot.timeline-view__legend-dot--pending
                span {{ $t('Pending') }}
            .timeline-view__legend-item
                .timeline-view__legend-dot.timeline-view__legend-dot--confirmed
                span {{ $t('Confirmed') }}
            .timeline-view__legend-item
                .timeline-view__legend-dot.timeline-view__legend-dot--seated
                span {{ $t('Seated') }}
            .timeline-view__legend-item
                .timeline-view__legend-dot.timeline-view__legend-dot--completed
                span {{ $t('Completed') }}
            .timeline-view__legend-item
                .timeline-view__legend-dot.timeline-view__legend-dot--cancelled
                span {{ $t('Cancelled') }}

        //- Timeline grid
        el-card(shadow="never")
            .timeline-scrollbar-top(ref="scrollbarTop")
                .timeline-scrollbar-top__inner(ref="scrollbarInner")
            .timeline-grid(ref="timelineGrid")
                //- Time header
                .timeline-grid__header(:style="gridRowStyle")
                    .timeline-grid__label-cell {{ $t('Table') }}
                    .timeline-grid__time-cell(
                        v-for="slot in timeSlots"
                        :key="slot"
                        :class="{ 'timeline-grid__time-cell--hour': isFullHour(slot) }"
                    ) {{ isFullHour(slot) ? slot : '' }}

                //- Unassigned reservations row
                .timeline-grid__row.timeline-grid__row--unassigned(v-if="unassignedReservations.length" :style="gridRowStyle")
                    .timeline-grid__label-cell
                        .timeline-grid__table-label
                            i.el-icon-warning-outline(style="color:#e6a23c;margin-right:2px")
                            | Nicht
                            br
                            | zugewiesen
                    .timeline-grid__slots-container
                        .timeline-grid__slot(v-for="slot in timeSlots" :key="slot")
                        .timeline-grid__reservation(
                            v-for="res in unassignedReservations"
                            :key="res.id"
                            :class="['timeline-grid__reservation--' + res.status, { 'timeline-grid__reservation--dragging': draggingResId === res.id }]"
                            :style="getReservationStyle(res)"
                            :draggable="canUpdateReservation"
                            @dragstart="onResDragStart(res, $event)"
                            @dragend="onResDragEnd"
                            @click.stop="showReservationMenu(res, $event)"
                        )
                            .timeline-grid__reservation-text {{ res.time }} {{ res.guestName || 'Walk-in' }} ({{ res.partySize }})

                //- Room groups
                template(v-for="room in roomsWithTables")
                    //- Room header
                    .timeline-grid__room-header(:key="'room-' + room.id")
                        strong {{ room.name }}

                    //- Table rows
                    .timeline-grid__row(
                        v-for="table in room.tables"
                        :key="table.id"
                        :style="gridRowStyle"
                    )
                        .timeline-grid__label-cell
                            .timeline-grid__table-label
                                span.timeline-grid__table-number {{ table.number }}
                                span.timeline-grid__table-seats
                                    i.el-icon-fa-user.fas
                                    |  {{ table.seats }}

                        .timeline-grid__slots-container
                            //- Time slot grid lines
                            .timeline-grid__slot(
                                v-for="slot in timeSlots"
                                :key="slot"
                                :data-slot-key="table.id + '|' + slot"
                                :class="{ 'timeline-grid__slot--hour': isFullHour(slot), 'timeline-grid__slot--now': isSlotCurrent(slot) }"
                                @click="showSlotMenu(table, slot, $event)"
                                @dragover.prevent="onSlotDragOver(table, slot, $event)"
                                @dragleave="onSlotDragLeave($event)"
                                @drop.prevent="onSlotDrop(table, slot, $event)"
                            )

                            //- Reservation blocks
                            .timeline-grid__reservation(
                                v-for="res in getTableReservations(table.id)"
                                :key="res.id"
                                :class="'timeline-grid__reservation--' + res.status"
                                :style="getReservationStyle(res)"
                                :draggable="canUpdateReservation"
                                @dragstart="onResDragStart(res, $event)"
                                @dragend="onResDragEnd"
                                @click.stop="showReservationMenu(res, $event)"
                            )
                                .timeline-grid__reservation-text
                                    | {{ res.time }} {{ res.guestName || 'Walk-in' }} ({{ res.partySize }})

                //- Current time indicator
                .timeline-grid__now-line(v-if="showNowLine" :style="nowLineStyle")

                //- Reservation mini menu
                .timeline-reservation-menu(
                    v-if="reservationMenu.visible"
                    :style="{ left: reservationMenu.x + 'px', top: reservationMenu.y + 'px' }"
                    @click.stop
                )
                    .timeline-reservation-menu__header
                        strong {{ reservationMenu.reservation && (reservationMenu.reservation.guestName || 'Walk-in') }}
                        span.timeline-reservation-menu__time(v-if="reservationMenu.reservation")  · {{ reservationMenu.reservation.time }}
                        el-button(type="text" icon="el-icon-close" size="mini" @click="closeReservationMenu")
                    .timeline-reservation-menu__actions
                        el-button(v-if="canUpdateReservation && canChangeMenuReservationStatus" size="mini" type="primary" @click="menuAction('seated')") Platziert
                        el-button(v-if="canUpdateReservation && canChangeMenuReservationStatus" size="mini" type="success" @click="menuAction('completed')") Abgeschlossen
                        el-button(v-if="canUpdateReservation && canChangeMenuReservationStatus" size="mini" type="danger" @click="menuAction('cancelled')") Storniert
                        el-button(v-if="canUpdateReservation" size="mini" icon="el-icon-edit" @click="menuEdit") Bearbeiten

                //- Quick slot menu
                .timeline-slot-menu(
                    v-if="slotMenu.visible"
                    :style="{ left: slotMenu.x + 'px', top: slotMenu.y + 'px' }"
                    @click.stop
                )
                    .timeline-slot-menu__header
                        span {{ slotMenu.slot }} · Tisch {{ slotMenu.table && slotMenu.table.number }}
                        el-button(type="text" icon="el-icon-close" size="mini" @click="closeSlotMenu")
                    .timeline-slot-menu__actions
                        el-button(v-if="canCreateReservation" type="primary" size="small" @click="quickSeat") Sofort belegen
                        el-button(v-if="canCreateReservation" size="small" @click="openNewReservationForm") Neue Reservierung
</template>

<i18n>
{
    "de": {
        "Select date": "Datum wählen",
        "Reservations": "Reservierungen",
        "Guests": "Gäste",
        "Pending": "Ausstehend",
        "Confirmed": "Bestätigt",
        "Seated": "Platziert",
        "Completed": "Abgeschlossen",
        "Cancelled": "Storniert",
        "Table": "Tisch"
    }
}
</i18n>

<script>
import _ from 'lodash';
import router from '@/router';
import { checkRestrictions } from '@/helpers';
import { useTableReservationStore, useTableReservationFloorPlanStore } from '@/stores';
import { isTerminalReservationStatus } from '@/extensions/tableReservation/store/utils';
import timelineMixin, { TIMELINE_SLOT_MINUTES } from '@/extensions/tableReservation/views/composables/timelineMixin';

const SLOT_WIDTH = 40; // px per 30-min slot
const LABEL_WIDTH = 100; // px for table label column
const DRAG_OVER_CLASS = 'timeline-grid__slot--drag-over';

export default {
    name: 'TimelineView',
    mixins: [timelineMixin],
    data: function () {
        return {
            reservationMenu: {
                visible: false,
                reservation: null,
                x: 0,
                y: 0
            },
            slotMenu: {
                visible: false,
                table: null,
                slot: null,
                x: 0,
                y: 0
            },
            menuDismissHandler: null,
            slotDismissHandler: null
        };
    },
    computed: {
        reservationStore: function () {
            return useTableReservationStore();
        },
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        roomsWithTables: function () {
            return this.floorPlanStore.rooms.map((room) => {
                return {
                    id: room.id,
                    name: room.name,
                    tables: this.floorPlanStore.tablesByRoom(room.id)
                };
            }).filter((room) => { return room.tables.length > 0; });
        },
        reservationsForDate: function () {
            return this.reservationStore.reservationsByDate(this.selectedDate);
        },
        selectedDate: {
            get: function () {
                return this.reservationStore.filters.date;
            },
            set: function (value) {
                this.reservationStore.setFilterDate(value).catch((error) => {
                    this.showActionError(error);
                });
            }
        },
        totalGuests: function () {
            return this.reservationsForDate
                .filter((r) => { return r.status !== 'cancelled' && r.status !== 'no_show'; })
                .reduce((sum, r) => { return sum + r.partySize; }, 0);
        },
        unassignedReservations: function () {
            return this.reservationsForDate.filter((r) => {
                return (!r.tableIds || !r.tableIds.length) && r.status !== 'cancelled' && r.status !== 'no_show';
            });
        },
        showNowLine: function () {
            return this.isDateToday(this.selectedDate);
        },
        gridRowStyle: function () {
            return { minWidth: (LABEL_WIDTH + this.timeSlots.length * SLOT_WIDTH) + 'px' };
        },
        nowLineStyle: function () {
            if (!this.showNowLine) return { display: 'none' };
            const pos = this.timeToPosition(this.currentTime);
            if (pos < 0) return { display: 'none' };
            return {
                left: (LABEL_WIDTH + pos) + 'px'
            };
        },
        canChangeMenuReservationStatus: function () {
            const reservation = this.reservationMenu.reservation;
            return !reservation || !isTerminalReservationStatus(reservation.status);
        },
        canCreateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'create');
        },
        canUpdateReservation: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'update');
        }
    },
    mounted: async function () {
        try {
            await this.reservationStore.ensureBoard();
        } catch (error) {
            this.showActionError(error);
        }
        this.$nextTick(() => {
            this._syncScrollbarWidth();
            const grid = this.$refs.timelineGrid;
            const top = this.$refs.scrollbarTop;
            this._onGridScroll = () => { top.scrollLeft = grid.scrollLeft; };
            this._onTopScroll = () => { grid.scrollLeft = top.scrollLeft; };
            grid.addEventListener('scroll', this._onGridScroll);
            top.addEventListener('scroll', this._onTopScroll);
            this._resizeHandler = () => { this._syncScrollbarWidth(); };
            window.addEventListener('resize', this._resizeHandler);
        });
    },
    updated: function () {
        this._syncScrollbarWidth();
    },
    beforeDestroy: function () {
        const grid = this.$refs.timelineGrid;
        const top = this.$refs.scrollbarTop;
        if (grid && this._onGridScroll) grid.removeEventListener('scroll', this._onGridScroll);
        if (top && this._onTopScroll) top.removeEventListener('scroll', this._onTopScroll);
        if (this._resizeHandler) {
            window.removeEventListener('resize', this._resizeHandler);
            this._resizeHandler = null;
        }
        this._clearDragOverHighlight();
        this.closeReservationMenu();
        this.closeSlotMenu();
    },
    methods: {
        _syncScrollbarWidth: function () {
            if (this.$refs.scrollbarInner && this.$refs.timelineGrid) {
                this.$refs.scrollbarInner.style.width = this.$refs.timelineGrid.scrollWidth + 'px';
            }
        },
        isSlotCurrent: function (slot) {
            return this.isCurrentSlot(slot, this.selectedDate);
        },
        timeToPosition: function (time) {
            return this.slotIndex(time) * SLOT_WIDTH;
        },
        getTableReservations: function (tableId) {
            return this.reservationStore.reservationsByTableAndDate(tableId, this.selectedDate);
        },
        getReservationStyle: function (res) {
            const { durationMins } = this.getReservationDurationMinutes(res, this.reservationsForDate, this.selectedDate);
            return {
                left: this.timeToPosition(res.time) + 'px',
                width: ((durationMins / TIMELINE_SLOT_MINUTES) * SLOT_WIDTH) + 'px'
            };
        },
        editReservation: function (res) {
            router.push({ name: 'table-reservation-form', params: { id: res.id }, query: { from: 'timeline' } });
        },
        createAtSlot: function (table, slot) {
            const store = this.reservationStore;
            store.resetForm();
            store.setFormField('tableIds', [table.id]);
            store.setFormField('date', this.selectedDate);
            store.setFormField('time', slot);
            router.push({ name: 'table-reservation-form' });
        },
        // Reservation mini menu
        showReservationMenu: function (res, event) {
            if (!this.canUpdateReservation) return;
            this.closeSlotMenu();
            this.reservationMenu = {
                visible: true,
                reservation: res,
                x: Math.min(event.clientX + 8, window.innerWidth - 230),
                y: Math.min(event.clientY + 8, window.innerHeight - 160)
            };
            this.menuDismissHandler = () => { this.closeReservationMenu(); };
            document.addEventListener('click', this.menuDismissHandler, { once: true });
        },
        closeReservationMenu: function () {
            this.reservationMenu.visible = false;
            if (this.menuDismissHandler) {
                document.removeEventListener('click', this.menuDismissHandler);
                this.menuDismissHandler = null;
            }
        },
        menuAction: async function (status) {
            if (this.reservationMenu.reservation) {
                try {
                    await this.reservationStore.updateStatus(this.reservationMenu.reservation.id, status);
                } catch (error) {
                    this.showActionError(error);
                }
            }
            this.closeReservationMenu();
        },
        menuEdit: function () {
            const res = this.reservationMenu.reservation;
            this.closeReservationMenu();
            if (res) this.editReservation(res);
        },
        // Quick slot menu
        showSlotMenu: function (table, slot, event) {
            if (!this.canCreateReservation) return;
            event.stopPropagation();
            this.closeReservationMenu();
            this.slotMenu = {
                visible: true,
                table: table,
                slot: slot,
                x: Math.min(event.clientX + 8, window.innerWidth - 230),
                y: Math.min(event.clientY + 8, window.innerHeight - 120)
            };
            this.slotDismissHandler = () => { this.closeSlotMenu(); };
            document.addEventListener('click', this.slotDismissHandler, { once: true });
        },
        closeSlotMenu: function () {
            this.slotMenu.visible = false;
            if (this.slotDismissHandler) {
                document.removeEventListener('click', this.slotDismissHandler);
                this.slotDismissHandler = null;
            }
        },
        quickSeat: async function () {
            const table = this.slotMenu.table;
            const slot = this.slotMenu.slot;
            this.closeSlotMenu();

            try {
                await this.reservationStore.createReservation({
                    tableIds: [table.id],
                    date: this.selectedDate,
                    time: slot,
                    guestName: 'Walk-in',
                    status: 'seated',
                    partySize: 2,
                    duration: 90
                });
            } catch (error) {
                this.showActionError(error);
            }
        },
        openNewReservationForm: function () {
            const table = this.slotMenu.table;
            const slot = this.slotMenu.slot;
            this.closeSlotMenu();
            this.createAtSlot(table, slot);
        },
        onResDragEnd: function () {
            this.draggingResId = null;
            this._clearDragOverHighlight();
        },
        _clearDragOverHighlight: function () {
            if (this._currentDragOverEl) {
                this._currentDragOverEl.classList.remove(DRAG_OVER_CLASS);
                this._currentDragOverEl = null;
            }
        },
        // Drag-over highlight is applied via direct DOM manipulation to avoid
        // re-rendering all (tables x slots) cells on every dragover tick.
        onSlotDragOver: function (table, slot, event) {
            if (!this.canUpdateReservation || !this.draggingResId) return;
            const el = event.currentTarget;
            if (this._currentDragOverEl === el) return;
            if (this._currentDragOverEl) {
                this._currentDragOverEl.classList.remove(DRAG_OVER_CLASS);
            }
            el.classList.add(DRAG_OVER_CLASS);
            this._currentDragOverEl = el;
        },
        onSlotDragLeave: function (event) {
            const el = event.currentTarget;
            if (this._currentDragOverEl === el) {
                el.classList.remove(DRAG_OVER_CLASS);
                this._currentDragOverEl = null;
            }
        },
        onSlotDrop: async function (table, slot, event) {
            if (!this.canUpdateReservation) return;
            this._clearDragOverHighlight();
            const resId = this.draggingResId || event.dataTransfer.getData('text/plain');
            if (resId) {
                const reservation = this.reservationStore.reservations.find((candidate) => {
                    return candidate.id === String(resId);
                });
                const tableIds = _.uniq([].concat(_.get(reservation, 'tableIds', []), [String(table.id)]));

                try {
                    await this.reservationStore.moveReservation(resId, {
                        tableIds: tableIds,
                        time: slot
                    });
                } catch (error) {
                    this.showActionError(error);
                }
            }
            this.draggingResId = null;
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

$slot-width: 40px
$label-width: 100px
$row-height: 40px

.timeline-view
    &__legend
        display: flex
        gap: 15px
        margin-bottom: 15px

        &-item
            display: flex
            align-items: center
            gap: 5px
            font-size: 0.8rem
            color: #606266

        &-dot
            width: 12px
            height: 12px
            border-radius: 3px

            &--pending
                background: #e6a23c
            &--confirmed
                background: #67c23a
            &--seated
                background: #409eff
            &--completed
                background: #909399
            &--cancelled
                background: #f56c6c

.timeline-scrollbar-top
    overflow-x: scroll
    overflow-y: hidden
    height: 12px
    margin-bottom: 2px

    &::-webkit-scrollbar
        height: 12px

    &::-webkit-scrollbar-track
        background: #f0f0f0
        border-radius: 6px

    &::-webkit-scrollbar-thumb
        background: #c0c4cc
        border-radius: 6px
        &:hover
            background: #909399

    &__inner
        height: 1px

.timeline-grid
    overflow-x: scroll
    position: relative

    &::-webkit-scrollbar
        height: 12px

    &::-webkit-scrollbar-track
        background: #f0f0f0
        border-radius: 6px

    &::-webkit-scrollbar-thumb
        background: #c0c4cc
        border-radius: 6px
        &:hover
            background: #909399

    &__header
        display: flex
        border-bottom: 2px solid #dcdfe6
        position: sticky
        top: 0
        background: #fff
        z-index: 5

    &__label-cell
        flex: 0 0 $label-width
        min-width: 0
        padding: 8px 10px
        font-size: 0.8rem
        font-weight: 600
        color: #606266
        overflow: hidden
        border-right: 1px solid #ebeef5
        position: sticky
        left: 0
        background: #fff
        z-index: 3

    &__header &__label-cell
        z-index: 6
        background: #fff

    &__time-cell
        flex: 0 0 $slot-width
        text-align: center
        font-size: 0.7rem
        color: #909399
        padding: 4px 0
        border-left: 1px solid #f0f0f0

        &--hour
            border-left: 1px solid #dcdfe6
            color: #606266
            font-weight: 600

    &__room-header
        padding: 6px 10px
        background: #f5f7fa
        border-bottom: 1px solid #ebeef5
        font-size: 0.85rem
        position: sticky
        left: 0
        z-index: 3

    &__row
        display: flex
        border-bottom: 1px solid #f0f0f0
        height: $row-height

        &:hover
            background: #fafafa

    &__table-label
        display: flex
        align-items: center
        gap: 8px
        height: 100%

    &__table-number
        font-weight: 600
        font-size: 0.85rem

    &__table-seats
        font-size: 0.7rem
        color: #909399
        i
            font-size: 0.6rem

    &__slots-container
        position: relative
        display: flex
        flex: 1

    &__slot
        flex: 0 0 $slot-width
        border-left: 1px solid #f0f0f0
        cursor: pointer

        &--hour
            border-left: 1px solid #ebeef5

        &--now
            background: rgba(#f56c6c, 0.05)

        &--drag-over
            background: rgba($--color-primary, 0.12)

        &:hover
            background: rgba($--color-primary, 0.05)

    &__reservation
        position: absolute
        top: 3px
        height: $row-height - 6px
        border-radius: 4px
        padding: 0 6px
        display: flex
        align-items: center
        overflow: hidden
        cursor: pointer
        z-index: 2
        transition: opacity 0.2s

        &:hover
            opacity: 0.85

        &--dragging
            opacity: 0.4
            cursor: grabbing

        &--pending
            background: #fdf6ec
            border: 1px solid #e6a23c
            color: #e6a23c

        &--confirmed
            background: #f0f9eb
            border: 1px solid #67c23a
            color: #67c23a

        &--seated
            background: #ecf5ff
            border: 1px solid #409eff
            color: #409eff

        &--completed
            background: #f4f4f5
            border: 1px solid #909399
            color: #909399

        &--cancelled
            background: #fef0f0
            border: 1px solid #f56c6c
            color: #f56c6c
            opacity: 0.5
            text-decoration: line-through

    &__reservation-text
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        font-size: 0.75rem
        font-weight: 600

    &__now-line
        position: absolute
        top: 0
        bottom: 0
        width: 2px
        background: #f56c6c
        z-index: 4
        pointer-events: none

        &::before
            content: ''
            position: absolute
            top: -2px
            left: -4px
            width: 10px
            height: 10px
            border-radius: 50%
            background: #f56c6c

    &__row--unassigned
        background: #fffbf5

        .timeline-grid__label-cell
            color: #e6a23c

.timeline-reservation-menu
    position: fixed
    z-index: 2000
    background: #fff
    border: 1px solid #dcdfe6
    border-radius: 6px
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
    padding: 10px 12px
    min-width: 210px

    &__header
        display: flex
        justify-content: space-between
        align-items: center
        margin-bottom: 8px
        font-size: 0.85rem

    &__time
        color: #909399
        font-weight: normal

    &__actions
        display: flex
        flex-direction: column
        gap: 4px
        .el-button
            display: block
            width: 100%
            margin: 0

.timeline-slot-menu
    position: fixed
    z-index: 2000
    background: #fff
    border: 1px solid #dcdfe6
    border-radius: 6px
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
    padding: 12px

    &__header
        display: flex
        justify-content: space-between
        align-items: center
        font-size: 0.8rem
        color: #606266
        margin-bottom: 10px
        font-weight: 600

    &__actions
        display: flex
        flex-direction: column
        gap: 6px
        .el-button
            display: block
            width: 100%
            margin: 0
</style>
